/**
 * Iter XX.222 — Add a performance (manual entry).
 *
 * Deliberately tiny form. All optional fields stay optional — we do
 * NOT force format / venue / batting position / dismissal. Users can
 * enrich later.
 *
 * Batting-only, bowling-only, and all-rounder are all valid inputs.
 * At least one of batting.runs or bowling.wickets is required.
 */
import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { manualPerformance } from "../lib/performanceCapture";


export default function AddPerformanceForm({ open, onClose, onSaved }) {
  const [mode, setMode] = useState("batting"); // batting | bowling | both
  const [batting, setBatting] = useState({ runs: "", balls: "", not_out: false });
  const [bowling, setBowling] = useState({ overs: "", runs_conceded: "", wickets: "" });
  const [opposition, setOpposition] = useState("");
  const [dateIso, setDateIso] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(""); setBusy(true);
    try {
      const bat = (mode !== "bowling" && batting.runs !== "") ? {
        runs: parseInt(batting.runs, 10),
        balls: batting.balls === "" ? null : parseInt(batting.balls, 10),
        not_out: !!batting.not_out,
      } : null;
      const bowl = (mode !== "batting" && bowling.overs !== "" &&
                     bowling.runs_conceded !== "" && bowling.wickets !== "") ? {
        overs: parseFloat(bowling.overs),
        runs_conceded: parseInt(bowling.runs_conceded, 10),
        wickets: parseInt(bowling.wickets, 10),
      } : null;
      if (!bat && !bowl) { setErr("Enter a batting or bowling performance."); setBusy(false); return; }
      await manualPerformance({
        batting: bat, bowling: bowl,
        opposition: opposition.trim() || null,
        date_iso: dateIso || null,
      });
      onSaved?.();
      onClose?.();
    } catch (e2) {
      setErr(String(e2?.message || e2));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center px-0 sm:px-4"
      data-testid="add-performance-modal"
      onClick={onClose}
    >
      <div
        className="bg-neutral-950 border-t sm:border border-neutral-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5 relative"
        onClick={(e) => e.stopPropagation()}
        style={{
          // Iter XX.287 — Bottom-sheet MUST reserve the fixed
          // <MobileBottomNav>'s measured height (published by the nav as
          // `--gully-bottom-nav-h`, includes iOS safe-area). Prevents
          // the primary "Add performance" CTA from sitting behind the
          // z-80 nav on iPhone Safari 375–430px. Falls back to raw
          // safe-area when nav isn't mounted (desktop / marketing).
          paddingBottom: "calc(1.25rem + var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)))",
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-neutral-500 hover:text-white"
          data-testid="add-performance-close"
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <h3 className="text-lg font-semibold text-white mb-1"
             data-testid="add-performance-title">
          Add a performance
        </h3>
        <p className="text-xs text-neutral-500 mb-4">
          Just the basics. Details can be added later.
        </p>
        <div className="mb-4 flex bg-neutral-900 rounded-lg p-0.5 text-xs">
          {["batting", "bowling", "both"].map((k) => (
            <button
              key={k} type="button" onClick={() => setMode(k)}
              className={`flex-1 py-1.5 rounded-md capitalize ${
                mode === k ? "bg-emerald-700 text-white" : "text-neutral-400"
              }`}
              data-testid={`add-performance-mode-${k}`}
            >
              {k}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="space-y-3">
          {mode !== "bowling" && (
            <div className="grid grid-cols-3 gap-2"
                  data-testid="add-performance-batting-fields">
              <label className="col-span-1 text-xs text-neutral-500">Runs *</label>
              <input
                type="number" min="0" max="500" required={mode !== "bowling"}
                value={batting.runs}
                onChange={(e) => setBatting({...batting, runs: e.target.value})}
                className="col-span-2 bg-neutral-900 border border-neutral-800 rounded-md px-2 py-1.5 text-sm text-white"
                data-testid="add-performance-runs"
              />
              <label className="col-span-1 text-xs text-neutral-500">Balls</label>
              <input
                type="number" min="0" max="1000"
                value={batting.balls}
                onChange={(e) => setBatting({...batting, balls: e.target.value})}
                className="col-span-2 bg-neutral-900 border border-neutral-800 rounded-md px-2 py-1.5 text-sm text-white"
                placeholder="optional"
                data-testid="add-performance-balls"
              />
              <label className="col-span-3 flex items-center gap-2 text-xs text-neutral-400">
                <input
                  type="checkbox" checked={batting.not_out}
                  onChange={(e) => setBatting({...batting, not_out: e.target.checked})}
                  data-testid="add-performance-not-out"
                />
                Not out
              </label>
            </div>
          )}
          {mode !== "batting" && (
            <div className="grid grid-cols-3 gap-2"
                  data-testid="add-performance-bowling-fields">
              <label className="col-span-1 text-xs text-neutral-500">Overs *</label>
              <input
                type="number" step="0.1" min="0" max="100"
                required={mode !== "batting"}
                value={bowling.overs}
                onChange={(e) => setBowling({...bowling, overs: e.target.value})}
                className="col-span-2 bg-neutral-900 border border-neutral-800 rounded-md px-2 py-1.5 text-sm text-white"
                data-testid="add-performance-overs"
              />
              <label className="col-span-1 text-xs text-neutral-500">Runs *</label>
              <input
                type="number" min="0" max="500"
                required={mode !== "batting"}
                value={bowling.runs_conceded}
                onChange={(e) => setBowling({...bowling, runs_conceded: e.target.value})}
                className="col-span-2 bg-neutral-900 border border-neutral-800 rounded-md px-2 py-1.5 text-sm text-white"
                data-testid="add-performance-runs-conceded"
              />
              <label className="col-span-1 text-xs text-neutral-500">Wickets *</label>
              <input
                type="number" min="0" max="10"
                required={mode !== "batting"}
                value={bowling.wickets}
                onChange={(e) => setBowling({...bowling, wickets: e.target.value})}
                className="col-span-2 bg-neutral-900 border border-neutral-800 rounded-md px-2 py-1.5 text-sm text-white"
                data-testid="add-performance-wickets"
              />
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-neutral-800">
            <label className="col-span-1 text-xs text-neutral-500">Opposition</label>
            <input
              type="text" maxLength="120" value={opposition}
              onChange={(e) => setOpposition(e.target.value)}
              placeholder="optional"
              className="col-span-2 bg-neutral-900 border border-neutral-800 rounded-md px-2 py-1.5 text-sm text-white"
              data-testid="add-performance-opposition"
            />
            <label className="col-span-1 text-xs text-neutral-500">Date</label>
            <input
              type="date" value={dateIso}
              onChange={(e) => setDateIso(e.target.value)}
              className="col-span-2 bg-neutral-900 border border-neutral-800 rounded-md px-2 py-1.5 text-sm text-white"
              data-testid="add-performance-date"
            />
          </div>
          {err && <p className="text-xs text-red-400"
                    data-testid="add-performance-error">{err}</p>}
          <button
            type="submit" disabled={busy}
            className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-lg inline-flex items-center justify-center gap-2"
            data-testid="add-performance-save"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Save
          </button>
        </form>
      </div>
    </div>
  );
}
